/**
 * NotificationSettingsDialog — admin-only panel to manage Web Push.
 *
 * Why this exists: the "ativar notificações" banner only shows once and
 * then hides for 7 days (or forever if the admin dismissed it on a
 * different device).  On iOS the PWA must be installed to even reach
 * `pushSupported`, so a user who installed later needs an explicit
 * entry point to enable push.  This dialog is reachable from the user
 * menu at any time and shows:
 *   • current permission state on THIS device,
 *   • an "Ativar" button that triggers the native prompt + backend
 *     subscription,
 *   • a "Enviar teste" button that bounces a push through the backend
 *     back to all admin devices,
 *   • the list of currently-subscribed admin devices with vendor and a
 *     one-click remove.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone, Send, Loader2, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import {
  pushSupported, getPushPermission, enablePushForAdmin, disablePushForAdmin,
} from "@/lib/push";
import { isIOS, isStandalone } from "@/components/InstallAppDialog";
import SchoolPushList, { fmtSeen } from "@/components/SchoolPushList";

const vendorBadge = (v) => {
  if (v === "iOS")           return { bg: "bg-slate-800",   label: "iPhone" };
  if (v === "Android/Chrome") return { bg: "bg-emerald-600", label: "Android/Chrome" };
  if (v === "Firefox")       return { bg: "bg-orange-600",  label: "Firefox" };
  return { bg: "bg-slate-500", label: "Navegador" };
};

export default function NotificationSettingsDialog({ open, onOpenChange }) {
  const [perm, setPerm]           = useState("default");
  const [thisSubEndpoint, setThis] = useState(null);
  const [devices, setDevices]     = useState([]);
  const [busy, setBusy]           = useState(false);
  const [testing, setTesting]     = useState(false);
  const ios        = isIOS();
  const standalone = isStandalone();
  const needsPwaFirst = ios && !standalone;

  const loadState = useCallback(async () => {
    try { setPerm(await getPushPermission()); } catch {}
    try {
      if (pushSupported()) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setThis(sub?.endpoint || null);
      } else {
        setThis(null);
      }
    } catch { setThis(null); }
    try {
      const r = await api.get("/admin/push/devices");
      setDevices(r.data || []);
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => { if (open) loadState(); }, [open, loadState]);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await enablePushForAdmin();
      if (r.ok) {
        toast.success("Notificações ativadas neste dispositivo!");
      } else if (r.reason === "denied") {
        toast.error("Permissão negada — habilite nas configurações do navegador/Safari.");
      } else if (r.reason === "unsupported") {
        toast.error(needsPwaFirst
          ? "No iPhone: instale o app na tela inicial primeiro."
          : "Este navegador não suporta notificações push.");
      } else {
        toast.error("Não foi possível ativar push.");
      }
      await loadState();
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Desativar notificações neste dispositivo?")) return;
    setBusy(true);
    try {
      await disablePushForAdmin();
      toast("Notificações desativadas neste dispositivo");
      await loadState();
    } finally { setBusy(false); }
  };

  const removeDevice = async (endpoint) => {
    if (!window.confirm("Remover este dispositivo? Ele deixará de receber push.")) return;
    try {
      await api.post("/admin/push/unsubscribe", { endpoint });
      toast("Dispositivo removido");
      await loadState();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/push/test");
      if (r.data?.dropped) {
        toast.success(`Teste enviado — ${r.data.remaining} dispositivos ativos (${r.data.dropped} expirados removidos).`);
      } else {
        toast.success(`Teste enviado para ${r.data?.sent_to || 0} dispositivo(s). Confira a tela/barra de notificações.`);
      }
      await loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível enviar teste");
    } finally { setTesting(false); }
  };

  const subscribedHere = thisSubEndpoint && devices.some((d) => d.endpoint === thisSubEndpoint);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="notif-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-[#1e3a8a] flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#7c3aed]" />
            Notificações
          </DialogTitle>
          <DialogDescription>
            Receba alertas no celular quando a escola aprovar, enviar observação ou interagir com um orçamento — mesmo com o app fechado.
          </DialogDescription>
        </DialogHeader>

        {/* iOS pre-check */}
        {needsPwaFirst && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-[12.5px] text-amber-900">
            <Smartphone className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold">iPhone precisa do app instalado</p>
              <p className="text-[11.5px] mt-0.5">
                Abra no <strong>Safari</strong>, toque no <strong>compartilhar</strong> e em <strong>"Adicionar à Tela de Início"</strong>. Depois, abra o AMC pelo ícone instalado e volte aqui.
              </p>
            </div>
          </div>
        )}

        {/* Status card for this device */}
        <div className={`rounded-lg border p-3 flex items-start gap-3 ${
          subscribedHere ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
        }`} data-testid="notif-status">
          {subscribedHere ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-slate-900">
              {subscribedHere ? "Este dispositivo está ativo" : "Este dispositivo ainda não recebe push"}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Permissão do navegador: <strong>{perm === "granted" ? "concedida" : perm === "denied" ? "bloqueada" : "pendente"}</strong>
              {perm === "denied" && " — reative nas configurações do navegador."}
            </p>
            <div className="flex gap-2 mt-2 flex-wrap">
              {!subscribedHere ? (
                <Button
                  size="sm"
                  onClick={enable}
                  disabled={busy || needsPwaFirst || perm === "denied"}
                  className="h-7 px-3 text-[11.5px] bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                  data-testid="notif-enable-btn"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3 mr-1" />}
                  Ativar neste dispositivo
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={disable}
                  disabled={busy}
                  className="h-7 px-3 text-[11.5px]"
                  data-testid="notif-disable-btn"
                >
                  <BellOff className="w-3 h-3 mr-1" />
                  Desativar aqui
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={sendTest}
                disabled={testing || devices.length === 0}
                className="h-7 px-3 text-[11.5px]"
                data-testid="notif-test-btn"
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Enviar teste
              </Button>
            </div>
          </div>
        </div>

        {/* Devices list */}
        <div className="space-y-2">
          <p className="text-[11px] font-black uppercase text-slate-500 tracking-widest">
            Meus aparelhos ({devices.length})
          </p>
          {devices.length === 0 ? (
            <p className="text-[12px] text-slate-400 italic">Nenhum dispositivo ativo. Toque em "Ativar" acima.</p>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {devices.map((d) => {
                const vb = vendorBadge(d.vendor);
                const isCurrent = thisSubEndpoint === d.endpoint;
                return (
                  <li key={d.endpoint} className="px-3 py-2 flex items-center gap-2 text-[12px]" data-testid={`notif-device-${d.endpoint_fingerprint}`}>
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${vb.bg} text-white text-[10px] font-black shrink-0`}>
                      <Smartphone className="w-3 h-3" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 truncate">
                        {d.label || vb.label}{isCurrent && <span className="ml-1 text-emerald-600 text-[10px]">(este)</span>}
                      </p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {[d.env, fmtSeen(d.updated_at) && `ativo em ${fmtSeen(d.updated_at)}`].filter(Boolean).join(" · ") || `…${d.endpoint_fingerprint}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDevice(d.endpoint)}
                      className="text-rose-500 hover:text-rose-700 p-1"
                      aria-label="Remover"
                      data-testid={`notif-remove-${d.endpoint_fingerprint}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <SchoolPushList open={open} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
